import React from 'react'
import { MapContainer, TileLayer, Marker, Tooltip, Circle, useMap, useMapEvents } from 'react-leaflet'
import L from 'leaflet'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

const CITY_POS = {
  'yaoundé': { lat: 3.8619, lng: 11.5214 },
  'yaounde': { lat: 3.8619, lng: 11.5214 },
  'douala': { lat: 4.0511, lng:  9.7679 },
  'berlin': { lat: 52.5208, lng: 13.4095 },
  'bafoussam': { lat: 5.4778, lng: 10.4170 },
  'limbé': { lat: 4.0173, lng:  9.2149 },
  'limbe': { lat: 4.0173, lng:  9.2149 },
}

const houseIcon = L.divIcon({
  className: 'house-pin',
  html: `
    <div style="
      width:28px;height:28px;border-radius:50%;
      display:flex;align-items:center;justify-content:center;
      background:#1e4db7;color:#fff;border:2px solid #fff;
      box-shadow:0 4px 10px rgba(0,0,0,.25);
      ">
      🏠
    </div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
})

function cityLabelIcon(name) {
  return L.divIcon({
    className: 'city-label',
    html: `
      <div style="
        transform: translate(-50%, -50%);
        background:rgba(255,255,255,0.9);
        border:1px solid #cfe0ff;
        border-radius:999px;
        padding:2px 8px;
        font-size:12px;
        font-weight:600;
        color:#1e3a8a;
        box-shadow:0 2px 8px rgba(0,0,0,.12);
        white-space:nowrap;
      ">${escapeHtml(name)}</div>`,
    iconSize: [0, 0],
    iconAnchor: [0, 0],
  })
}
function escapeHtml(s=''){
  return s.replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]))
}

/** Haversine (km) */
function kmBetween(a, b) {
  const toRad = d => (d * Math.PI) / 180
  const R = 6371
  const dLat = toRad(b.lat - a.lat)
  const dLng = toRad(b.lng - a.lng)
  const lat1 = toRad(a.lat)
  const lat2 = toRad(b.lat)
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(s))
}

function FlyToCenter({ center, zoom = 10, enabled }) {
  const map = useMap()
  React.useEffect(() => {
    if (enabled && center) map.flyTo([center.lat, center.lng], zoom, { animate: true })
  }, [enabled, center, zoom, map])
  return null
}

function FitToPoints({ points, enabled }) {
  const map = useMap()
  React.useEffect(() => {
    if (!enabled || !points?.length) return
    const bounds = L.latLngBounds(points.map(p => [p.lat, p.lng]))
    if (bounds.isValid()) map.fitBounds(bounds.pad(0.2))
  }, [enabled, points, map])
  return null
}

function resolveCityCoords(query, items) {
  if (!query) return null
  const key = query.toLowerCase().trim()
  if (CITY_POS[key]) return { name: query, ...CITY_POS[key] }
  const byItem = items.find(it => (it.city || '').toLowerCase().includes(key))
  if (byItem?.lat != null && byItem?.lng != null) return { name: byItem.city, lat: byItem.lat, lng: byItem.lng }
  if (byItem?.city) {
    const alt = CITY_POS[byItem.city.toLowerCase()]
    if (alt) return { name: byItem.city, ...alt }
  }
  return null
}

function priceLabel(price) {
  if (!price) return ''
  const amt = Number(price.amount || 0) || ''
  const cur = price.currency || ''
  const unit = (price.unit || '').toLowerCase()
  const u = unit.includes('month') ? '/mois' : unit.includes('week') ? '/sem.' : unit.includes('night') ? '/nuit' : ''
  return [amt, cur, u].filter(Boolean).join(' ')
}

function toPoint(it) {
  const lat = Number(it.lat); const lng = Number(it.lng)
  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    return { id: it.id, title: it.title, price: it.price, city: it.city, lat, lng }
  }
  const alt = CITY_POS[(it.city || '').toLowerCase()]
  if (alt) return { id: it.id, title: it.title, price: it.price, city: it.city, lat: alt.lat, lng: alt.lng }
  return null
}

function CityLabels({ items = [], show = true, limit = 50 }) {
  const map = useMapEvents({
    moveend: () => setTick(t => t + 1),
    zoomend: () => setTick(t => t + 1),
  })
  const [tick, setTick] = React.useState(0)

  if (!show) return null
  const bounds = map.getBounds()

  const cityMap = new Map()
  for (const it of items) {
    const name = (it.city || '').trim()
    if (!name) continue
    if (!cityMap.has(name.toLowerCase())) {
      const lat = Number(it.lat); const lng = Number(it.lng)
      let pos = null
      if (Number.isFinite(lat) && Number.isFinite(lng)) pos = { lat, lng }
      else {
        const alt = CITY_POS[name.toLowerCase()]
        if (alt) pos = { ...alt }
      }
      if (pos) cityMap.set(name.toLowerCase(), { name, ...pos })
    }
  }
  for (const [key, pos] of Object.entries(CITY_POS)) {
    const label = key.charAt(0).toUpperCase() + key.slice(1)
    if (!cityMap.has(key)) cityMap.set(key, { name: label, ...pos })
  }

  const visibles = []
  cityMap.forEach((v) => {
    if (bounds.contains([v.lat, v.lng])) visibles.push(v)
  })

  const selected = visibles.slice(0, limit)

  return (
    <>
      {selected.map(c => (
        <Marker
          key={`city-${c.name}-${c.lat}-${c.lng}`}
          position={[c.lat, c.lng]}
          icon={cityLabelIcon(c.name)}
          interactive={false}
          keyboard={false}
          opacity={1}
        />
      ))}
    </>
  )
}

export default function MapView({ items = [] }) {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [query, setQuery] = React.useState('')
  const [radiusKm, setRadiusKm] = React.useState(100)
  const [center, setCenter] = React.useState({ lat: 4.0, lng: 11.5 }) // Default Cameroon
  const [showCityNames, setShowCityNames] = React.useState(true)

  const suggestions = React.useMemo(() => {
    const fromItems = Array.from(new Set(items.map(it => (it.city || '').toLowerCase()).filter(Boolean)))
    const fromRef = Object.keys(CITY_POS)
    return Array.from(new Set([...fromItems, ...fromRef])).sort()
  }, [items])

  function applyQuery(q) {
    const match = resolveCityCoords(q, items)
    if (match) setCenter({ lat: match.lat, lng: match.lng })
  }

  const points = React.useMemo(() => items.map(toPoint).filter(Boolean), [items])

  const visible = React.useMemo(() => {
    if (!query) return points
    return points.filter(p => kmBetween(center, { lat: p.lat, lng: p.lng }) <= radiusKm)
  }, [points, center, radiusKm, query])

  return (
    <div className="relative rounded-2xl overflow-hidden border border-blue-100 shadow-card">
      {/* Search bar*/}
      <div className="absolute z-[1000] top-3 left-3 right-3 md:left-4 md:right-4">
        <div className="flex items-center gap-2 bg-white/90 backdrop-blur px-3 py-2 rounded-xl shadow">
          <svg width="18" height="18" viewBox="0 0 24 24" className="opacity-70">
            <path d="M21 21l-4.35-4.35M10 18a8 8 0 1 1 0-16 8 8 0 0 1 0 16z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <input
            className="flex-1 bg-transparent outline-none"
            placeholder={t('map.searchPlaceholder')}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') applyQuery(query) }}
          />
          <button className="px-2 py-1 rounded-lg border border-blue-100 hover:bg-blue-50" onClick={() => applyQuery(query)}>
            OK
          </button>

          {/* Toggle show town name */}
          <label className="ml-2 flex items-center gap-2 text-sm bg-white/70 rounded-lg px-2 py-1 border border-blue-100">
            <input type="checkbox" checked={showCityNames} onChange={e=>setShowCityNames(e.target.checked)} />
            {t('map.showCities', 'Afficher les noms des villes')}
          </label>
        </div>

        {/* suggestions */}
        {query && (
          <div className="mt-2 bg-white/95 backdrop-blur rounded-xl border border-blue-100 shadow divide-y">
            {suggestions
              .filter(s => s.startsWith(query.toLowerCase()))
              .slice(0, 6)
              .map(s => (
                <button
                  key={s}
                  className="w-full text-left px-3 py-2 hover:bg-blue-50 rounded-xl"
                  onClick={() => { setQuery(s); applyQuery(s) }}
                >
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
          </div>
        )}
      </div>

      {/* Carte */}
      <MapContainer
        center={[center.lat, center.lng]}
        zoom={6}
        style={{ height: 420, width: '100%' }}
        scrollWheelZoom
      >
        <TileLayer
          attribution="&copy; OpenStreetMap"
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {/* */}
        <FlyToCenter center={center} zoom={query ? 11 : 6} enabled={!!query} />
        {/* Fit bounds auto if any search */}
        <FitToPoints points={points} enabled={!query && points.length > 0} />

        {/* rayon circle (if search) */}
        {query && (
          <Circle
            center={[center.lat, center.lng]}
            radius={radiusKm * 1000}
            pathOptions={{ color: '#1e4db7', fillColor: '#1e4db7', fillOpacity: 0.08 }}
          />
        )}

        {/* town Labels */}
        <CityLabels items={items} show={showCityNames} />

        {/* Marqueurs home */}
        {visible.map(p => (
          <Marker
            key={p.id}
            position={[p.lat, p.lng]}
            icon={houseIcon}
            eventHandlers={{ click: () => navigate(`/annonce/${p.id}`) }}
          >
            <Tooltip direction="top" offset={[0, -6]}>
              <div className="font-semibold">{p.title}</div>
              {!!p.price && <div>{priceLabel(p.price)}</div>}
              {p.city && <div className="text-xs opacity-80">{p.city}</div>}
            </Tooltip>
          </Marker>
        ))}
      </MapContainer>

      {/* Panneau radius */}
      <div className="absolute z-[1000] left-3 right-3 bottom-3 md:left-4 md:right-4">
        <div className="bg-white/95 backdrop-blur rounded-xl border border-blue-100 shadow p-3">
          <div className="text-sm font-semibold mb-1">{t('map.radiusTitle')}</div>
          <div className="flex items-center gap-3">
            <span className="tabular-nums w-16">{radiusKm} km</span>
            <input
              type="range"
              min="5"
              max="250"
              step="1"
              value={radiusKm}
              onChange={e => setRadiusKm(Number(e.target.value))}
              className="flex-1"
            />
          </div>
        </div>
      </div>
    </div>
  )
}
